import React, { useEffect, useRef, useState } from 'react';
import { unifiedTasksApi, UnifiedTask } from '../../api/unifiedTasks';
import { laneApi, TaskContent, LaneId } from '../../api/laneApi';
import { Claim } from '../../api/claims';
import { Button, ToastMessage } from '../../components/ui';
import { ClaimPickerModal } from './ClaimPickerModal';
import { MemberPickerModal } from './MemberPickerModal';
import { ArchiveReasonModal } from './ArchiveReasonModal';
import { ForwardComposer } from './ForwardComposer';
import { CancellationRingfencePanel } from './CancellationRingfencePanel';
import { RINGFENCE_TASK_SOURCE } from '../../api/cancellationRingfence';
import { parseBackendTimestamp } from './aging';
import { ActorLabel } from './actorLabel';
import { useAuth } from '../../context/AuthContext';
import { AutomationRunDrawer, replacementRunLabel } from '../../components/curation/automation/AutomationRunDrawer';
import {
  attachedRecentlyElsewhere,
  buildSingleAssignConfirmation,
  NO_REFERENCE_NOTE,
  normaliseClaimRef,
  pickPrefill,
  recordClaimAttach,
  resolveEmailRefs,
} from './claimRefMatch';
import { isLaneModalOpen } from './laneModals';
import EmailBodyFrame from '../../components/EmailBodyFrame';
import './lanes.css';

/**
 * The real task detail pane (Tasks Rebuild Task 12), replacing every lane's
 * `TODO(Task 12)` placeholder (ClientLane/LenderLane/PostLane/ManualLane's
 * `render*Detail` functions). Ported from the legacy Tasks.tsx view-task
 * modal (its "Email Content for Email Tasks" / "Manual Task Content" /
 * "Archive Information" sections and the assign/reassign/reply/complete/
 * archive action buttons), but as a self-contained pane instead of a modal.
 *
 * Data path (binding review-flag requirement): this component fetches by
 * task id directly -- `laneApi.getLaneTask(taskId)` for the task record
 * (subject/claim/status/AI fields/archive info -- everything a lane's slim
 * `LaneTaskRow` doesn't carry) and `laneApi.getTaskContent(taskId)` for
 * `emailContent`/`claudeAnalysis` (the content-table fields, Task 1's
 * backbone split) -- in parallel, every time `taskId` changes. It never
 * reads a parent lane's `rowsById` cache, so a bare `?selected=<id>` deep
 * link or a hard refresh renders correctly with no dependency on the lane
 * having already fetched that row into memory.
 *
 * Demo-findings fix: this used to call `unifiedTasksApi.getTask(taskId)`
 * (GET /ui/tasks/<id>), which resolves through
 * email_task_service.get_task_by_id -> EmailTask.to_dict(include_content=
 * True) -> the email_task_content side-table. On a DB where that table's
 * migration (migrations/2026-07-17-content-side-tables.sql) hasn't run
 * yet, that read raises, the route's bare `except Exception: pass` turns
 * it into a fall-through to the ID-verification lookup, and every task --
 * even one sitting right there in email_tasks -- came back 404 "Task not
 * found." `laneApi.getLaneTask` (GET /ui/lanes/task/<id>) reads only real
 * columns off email_tasks/archived_email_tasks and has no dependency on
 * that side-table at all; content is still fetched separately (and
 * lazily) via `laneApi.getTaskContent` below, unchanged.
 */

export interface TaskDetailPaneProps {
  taskId: string | null;
  /** Called after any action that changes the task (assign/reassign/
   * complete/archive/forward) so the owning lane can refetch its rows and
   * the shell can refetch its tab counts. */
  onChanged?: () => void;
  /** Shared one-line banner -- see LaneShell's `showToast` slot prop. */
  showToast?: (toast: ToastMessage) => void;
  /** Copy shown when nothing is selected. */
  emptyMessage?: string;
  /** Per-lane extra content rendered under the actions row once the task has
   * loaded -- e.g. PostLane's "Open in Post Tasks ->" link to the full
   * postal workflow page. */
  footerExtra?: (task: UnifiedTask) => React.ReactNode;
  /** Lets a lane keep its own subject rendering (e.g. LenderLane's AN:|ref
   * highlighting) instead of a plain string. Defaults to the raw subject. */
  renderSubject?: (subject: string | null) => React.ReactNode;
  /** The owning lane, forwarded to the internal MemberPickerModal's `lane`
   * prop so the single-task Assign/Reassign flow's "Erin — N pending"
   * counts are scoped the same way the bulk pickers are (reviewer blocker:
   * cross-lane totals -- see MemberPickerModal's `lane` prop doc). */
  lane?: LaneId;
  /** Lender task lists (spec 2026-10-08 §4.10): the claim the AI would attach
   * this email to. Pre-fills the claim picker and shows "Would attach to …"
   * while no claim is linked. */
  suggestedClaimRef?: string | null;
  /** Lane-specific buttons at the end of the actions row once the task has
   * loaded (Lender lane: the final-response check, "Make this an example"). */
  actionsExtra?: (task: UnifiedTask) => React.ReactNode;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseBackendTimestamp(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB');
}

// Final fix wave, finding I1 follow-up (2026-08-25): the same address
// claim_cancellation_service.py stamps on every internally-generated task
// it creates (70+ pre-cancellation hold, ring-fence review) --
// 'system@complexlaw.co.uk' -- see task_cancel_service.py's
// INTERNAL_SYSTEM_SENDER. A task carrying this sender was not triggered by
// a real client email, so its emailContent.receivedDate is a task-creation
// timestamp, not a client-supplied date, and must not reach the cancel
// modal's pre-fill either.
const INTERNAL_SYSTEM_SENDER = 'system@complexlaw.co.uk';

/** Builds the claim deep link, appending `&requestReceivedDate=YYYY-MM-DD`
 * (the cancel modal's `<input type="date">` format) when the task carries a
 * parseable `emailContent.receivedDate` AND is genuinely email-derived.
 * Exported for a direct unit test; a missing/unparseable date leaves the
 * link exactly as it was before the Task 6 fix round.
 *
 * Finding I1 follow-up: this pane is generic across every task type,
 * including the 70+ hold / ring-fence family, which also stamp
 * emailContent.receivedDate (at task-creation time, via the internal
 * system sender). `senderFrom` is `emailContent.from` -- present and not
 * the internal system sender means genuinely email-derived. An ABSENT
 * `from` is treated as NOT email-derived (conservative): we only trust the
 * date when we can positively identify a real sender, mirroring the
 * backend discriminator in task_cancel_service.py's
 * `_is_internally_generated`. */
export function buildClaimLinkHref(claimId: string, receivedDate: unknown, senderFrom?: unknown): string {
  const base = `/?openClaimId=${encodeURIComponent(claimId)}`;
  if (typeof senderFrom !== 'string' || !senderFrom.trim()) return base;
  if (senderFrom.trim().toLowerCase() === INTERNAL_SYSTEM_SENDER) return base;
  if (typeof receivedDate !== 'string' || !receivedDate.trim()) return base;
  const parsed = parseBackendTimestamp(receivedDate);
  if (Number.isNaN(parsed.getTime())) return base;
  const trimmed = parsed.toISOString().slice(0, 10);
  return `${base}&requestReceivedDate=${trimmed}`;
}

/** Attachment shape as it appears on `emailContent.attachments` -- looser
 * than unifiedTasks.ts's `UnifiedTask['attachments']` (that interface is for
 * the id_verification/top-level list) since this is the nested, per-email
 * array read straight off `TaskContent.emailContent`. */
interface EmailAttachment {
  name?: string;
  filename?: string;
  secureUrl?: string;
  /** Graph `contentId` for inline attachments (e.g. "image001.png@01D...").
   * Captured by ingestion since ticket #2 (2026-07-20); absent on rows
   * ingested before that change. */
  contentId?: string;
  isInline?: boolean;
  [key: string]: any;
}

const CID_IMG_SRC_RE = /(<img\b[^>]*\bsrc=["'])cid:([^"'\s]+)(["'])/gi;

/**
 * Demo finding (email renderer doesn't show images): Outlook/Graph HTML
 * bodies reference inline-pasted images as `<img src="cid:image001.png@...">`
 * -- a MIME content-id the browser cannot dereference on its own. Neither
 * the legacy Tasks.tsx modal nor ArchivedTasks.tsx ever resolved these (both
 * `dangerouslySetInnerHTML`'d `emailContent.body` completely raw -- verified
 * against the pre-deletion Tasks.tsx, commit 302f9e49^), so this was never a
 * working feature to regress from.
 *
 * Ticket #2 (2026-07-20): ingestion (caf_inbox_simple_processor.py,
 * email_listener_service.py) now captures inline attachments' bytes plus
 * Graph's `contentId`/`isInline` flags, so the exact MIME content-id is
 * available on `attachments[].contentId` for rows ingested after that
 * change. We match on that first -- decoded, case-insensitively, exact --
 * since a Content-ID is a MIME-unique identifier and this can never produce
 * a wrong match. For legacy rows (ingested before contentId was captured)
 * there is no contentId to match on, so we fall back to the same
 * best-effort filename/local-part heuristic as before: Outlook commonly
 * mints an inline image's Content-ID from its filename
 * (`cid:image001.png@01D...`), so this matches the `cid:` local-part against
 * `attachments[].name`/`filename`. An unresolved `cid:` reference is left
 * exactly as before (the browser's normal broken-image icon -- no worse
 * than pre-fix).
 */
export function resolveCidImages(html: string, attachments: EmailAttachment[] | undefined | null): string {
  if (!html || !attachments || attachments.length === 0) return html;
  return html.replace(CID_IMG_SRC_RE, (match, prefix, cidValue, suffix) => {
    const decodedCid = decodeURIComponent(cidValue);
    const localPart = decodedCid.split('@')[0].toLowerCase();

    // Prefer an exact contentId match -- unambiguous, no heuristics.
    const exact = attachments.find(
      (att) => !!att.contentId && att.contentId.toLowerCase() === decodedCid.toLowerCase()
    );
    const found =
      exact ||
      attachments.find((att) => {
        const name = (att.name || att.filename || '').toLowerCase();
        return !!name && (name === localPart || name === `${localPart}` || localPart.includes(name) || name.includes(localPart));
      });
    if (found?.secureUrl) {
      return `${prefix}${found.secureUrl}${suffix}`;
    }
    return match;
  });
}

const isCompletable = (status: string | undefined) => status === 'pending' || status === 'in_progress';

// T14 polish sweep: raw snake_case status values ("in_progress") aren't
// staff-safe copy -- the CSS modifier class still keys off the raw value
// (so styling is untouched), only the visible label is humanised.
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  archived: 'Archived',
};
const statusLabel = (status: string | undefined): string =>
  (status && STATUS_LABELS[status]) || status || 'Unknown';

export const TaskDetailPane: React.FC<TaskDetailPaneProps> = ({
  taskId, onChanged, showToast, emptyMessage, footerExtra, renderSubject, lane, suggestedClaimRef, actionsExtra,
}) => {
  const { isAdmin } = useAuth();
  const [showWorkflowRun, setShowWorkflowRun] = useState(false);
  const [task, setTask] = useState<UnifiedTask | null>(null);
  const [content, setContent] = useState<TaskContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [unassigningClaim, setUnassigningClaim] = useState(false);

  // Staff assignment restoration (fast-follow) -- this control used to be
  // gated on isAdmin because the backend action it drives (POST
  // /ui/team-tasks/bulk-assign) was require_role(['admin']) server-side.
  // Item 2 (lane-feedback batch 28/07, Ryan decision) removed that gate on
  // both sides -- the route is now require_auth, and the isAdmin check here
  // (and its useAuth import) is gone with it; see the button below.
  const [assignMemberOpen, setAssignMemberOpen] = useState(false);
  const [assigningMember, setAssigningMember] = useState(false);

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const [completing, setCompleting] = useState(false);

  const [forwardOpen, setForwardOpen] = useState(false);

  // Fetch by id directly -- see the data-path note in the file doc comment.
  // Latest-wins guard mirrors the lane bodies' `requestIdRef` pattern so a
  // slow response for a previously-selected task can never clobber the
  // currently-selected one.
  useEffect(() => {
    setShowWorkflowRun(false);
    // Wrong-claim attach guard: a claim picker opened for the previous task
    // must never carry over to the next one.
    setAssignOpen(false);
    if (!taskId) {
      setTask(null);
      setContent(null);
      setError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    // Do not leave the previous client's actionable detail mounted while the
    // newly selected task is loading. Besides being misleading, that window
    // could send an action to the old task after the operator clicked a new
    // row.
    setTask(null);
    setContent(null);
    setLoading(true);
    setError(null);
    Promise.allSettled([
      laneApi.getLaneTask(taskId),
      laneApi.getTaskContent(taskId),
    ]).then(([taskResult, contentResult]) => {
      if (requestId !== requestIdRef.current) return; // stale response, dropped
      if (taskResult.status === 'fulfilled') {
        setTask(taskResult.value);
      } else {
        setTask(null);
        const status = (taskResult.reason as any)?.response?.status;
        setError(status === 404 ? 'Task not found.' : 'Failed to load task.');
      }
      setContent(contentResult.status === 'fulfilled' ? contentResult.value : null);
    }).finally(() => {
      if (requestId === requestIdRef.current) setLoading(false);
    });
  }, [taskId]);

  // Single-key 'c' complete (binding keyboard requirement). There is no
  // reverse-of-complete endpoint on `unifiedTasksApi` (no "uncomplete"/
  // status-revert route exists), so a genuine undo isn't a safe, cheap
  // operation here -- an "Undo" toast would be a lie if clicking it can't
  // actually put the task back. Falls back to the confirm branch instead
  // (matching the existing bulk-complete confirm copy elsewhere in this
  // rebuild) rather than fabricating an undo that doesn't work.
  //
  // Ticket #403: Ctrl/Cmd+C on this pane was firing the complete hotkey
  // instead of copying the selected text (window.confirm popping up mid
  // copy). A `keydown` listener on `document` sees the *physical* key
  // ('c'/'C') regardless of modifiers, so it must explicitly bail on
  // Ctrl/Cmd/Alt+C -- those are copy/browser shortcuts, not the bare
  // single-key binding. Also bails when there's an active text selection
  // (`window.getSelection()`), since that's the signal the user is mid
  // copy-flow even on platforms/paths where the modifier check alone
  // wouldn't catch it (e.g. a context-menu Copy after a selection).
  //
  // Whole-branch review finding: this also must bail while any child modal
  // (Forward composer, claim/member picker, archive-reason) is open -- none
  // of those render their free-text fields as INPUT/TEXTAREA/SELECT in a
  // way the input-focus guard above always catches (e.g. ForwardComposer's
  // own internal fields, or simply a modal open with focus still on the
  // page body), so a bare 'c' typed while a modal is up could pop
  // window.confirm and complete the task out from under it.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'c' && e.key !== 'C') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return; // Ctrl/Cmd+C copies -- never completes (#403)
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (window.getSelection()?.toString()) return; // mid copy-flow -- don't hijack plain 'c' either
      if (forwardOpen || assignOpen || archiveOpen || assignMemberOpen) return; // a modal is up -- never hijack its input
      // Review Focus 3 (lender task lists): modals the lane owns -- the
      // final-response check, the teach form -- are invisible to the flags
      // above. Any lane modal or aria-modal dialog blocks the hotkey.
      if (isLaneModalOpen()) return;
      if (!task || !isCompletable(task.status)) return;
      // Whole-branch review finding 3: a 70+ pre-cancellation hold task's
      // only completion door is the ring-fence panel's own outcome buttons
      // -- the bare-'c' hotkey must be a no-op here too, same as the
      // Complete button it stands in for.
      const holdType = (content?.claudeAnalysis as any)?.extractedData?.holdType;
      if (holdType === 'pre_cancellation') return;
      // Same claim-link rule as the Complete button this stands in for (see
      // needsClaimLinkToComplete below -- recomputed here because that value
      // is derived further down, after the loading guards). A document-level
      // listener on a single bare letter is the easiest accidental
      // completion in the product, so it must be the FIRST thing gated, not
      // the last: without this it would sail straight past a disabled button
      // into a 409.
      if (lane === 'client' && !task.assignedClaimId && task.email_source !== RINGFENCE_TASK_SOURCE) return;
      e.preventDefault();
      handleComplete();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, content, forwardOpen, assignOpen, archiveOpen, assignMemberOpen, lane]);

  /** The completion itself, with no confirmation of its own. Shared by the
   * Complete button (which confirms first) and by any in-pane workflow that
   * has already taken its own explicit confirmation from the user -- e.g. the
   * ring-fence panel, whose "Waive fee"/"Charge anyway" modals ARE the
   * confirmation. Resolves true only when the task really was completed, so a
   * caller can tell a recorded-but-not-completed outcome apart from a clean
   * one. */
  const completeTaskNow = async (notes: string): Promise<boolean> => {
    if (!task) return false;
    setCompleting(true);
    try {
      await unifiedTasksApi.completeTask(task.id, notes);
      setTask((prev) => (prev ? { ...prev, status: 'completed', completedAt: new Date().toISOString() } : prev));
      showToast?.({ type: 'success', message: 'Task completed.' });
      onChanged?.();
      return true;
    } catch (err) {
      // Whole-branch review finding 3: this used to discard the server's
      // own message and always show the generic "Failed to complete task."
      // -- so a guarded door (e.g. the 409 from is_pre_cancellation_hold_
      // task on some other completion path) explained nothing. Same
      // `response.data.error` shape every other caller in this codebase
      // reads (see api/cancellationRingfence.ts's errorMessageFrom).
      const serverMessage = (err as { response?: { data?: { error?: unknown } } })
        ?.response?.data?.error;
      showToast?.({
        type: 'error',
        message: typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : 'Failed to complete task.',
      });
      return false;
    } finally {
      setCompleting(false);
    }
  };

  const handleComplete = async () => {
    if (!task) return;
    const ok = window.confirm('Complete this task?');
    if (!ok) return;
    await completeTaskNow('Task completed');
  };

  const handleAssignConfirm = async (claim: Claim) => {
    if (!task) return;
    // The task this assign is FOR. The selection can move while the request
    // is in flight; only that same task may show the new claim link.
    const assignedTaskId = task.id;
    setAssigning(true);
    try {
      if (task.assignedClaimId) {
        await unifiedTasksApi.reassignTask(assignedTaskId, claim.id);
      } else {
        await unifiedTasksApi.assignTask(assignedTaskId, claim.id, task.emailType);
      }
      recordClaimAttach(claim.id, [assignedTaskId]);
      setAssignOpen(false);
      setTask((prev) => (prev && prev.id === assignedTaskId ? { ...prev, assignedClaimId: claim.id } : prev));
      showToast?.({ type: 'success', message: `Assigned to claim ${claim.id}.` });
      onChanged?.();
    } catch {
      showToast?.({ type: 'error', message: 'Failed to assign task to claim.' });
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignClaim = async () => {
    const claimId = task?.assignedClaimId;
    if (!task || !claimId) return;
    const ok = window.confirm(
      `Remove this task from claim ${claimId}? It will stay in the task list.`
    );
    if (!ok) return;

    setUnassigningClaim(true);
    try {
      await unifiedTasksApi.unassignClaim(task.id, claimId);
      setTask((prev) => (prev ? {
        ...prev,
        assignedClaimId: undefined,
        clientId: undefined,
        clientName: undefined,
        clientEmail: undefined,
      } : prev));
      showToast?.({ type: 'success', message: `Removed from claim ${claimId}.` });
      onChanged?.();
    } catch (err) {
      const serverMessage = (err as { response?: { data?: { error?: unknown } } })
        ?.response?.data?.error;
      showToast?.({
        type: 'error',
        message: typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : 'Failed to remove task from claim.',
      });
    } finally {
      setUnassigningClaim(false);
    }
  };

  const handleAssignMemberConfirm = async (member: string | null) => {
    if (!task) return;
    setAssigningMember(true);
    try {
      await laneApi.assignMember([task.id], member);
      setAssignMemberOpen(false);
      setTask((prev) => (prev ? { ...prev, assignedToTeamMember: member } : prev));
      showToast?.({
        type: 'success',
        message: member ? `Assigned to ${member}.` : 'Unassigned.',
      });
      onChanged?.();
    } catch {
      showToast?.({ type: 'error', message: 'Failed to update staff assignment.' });
    } finally {
      setAssigningMember(false);
    }
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!task || reason.length < 10) return;
    setArchiving(true);
    try {
      await unifiedTasksApi.archiveTask(task.id, reason);
      setArchiveOpen(false);
      setTask((prev) => (prev ? { ...prev, archivedAt: new Date().toISOString(), archiveReason: reason } : prev));
      showToast?.({ type: 'success', message: 'Task archived.' });
      onChanged?.();
    } catch {
      showToast?.({ type: 'error', message: 'Failed to archive task.' });
    } finally {
      setArchiving(false);
    }
  };

  if (!taskId) {
    return <div className="lane-shell__placeholder">{emptyMessage || 'Select a task to view it here.'}</div>;
  }

  if (loading && !task) {
    return <div className="lane-shell__placeholder">Loading…</div>;
  }

  if (error && !task) {
    return <div className="task-detail__error">{error}</div>;
  }

  if (!task) {
    return <div className="lane-shell__placeholder">Loading…</div>;
  }

  // `content` comes from the dedicated GET /ui/tasks/<id>/content endpoint
  // (laneApi.getTaskContent) -- NOT `task.emailContent` from the general
  // GET /ui/tasks/<id> payload -- per the binding data-path requirement:
  // this pane exercises the same lazy content-fetch contract Task 7 built
  // for it, rather than depending on the heavier general endpoint always
  // embedding full content.
  const emailContent = content?.emailContent as Record<string, any> | null;
  const isEmailType = task.type === 'manual' ? false : true;
  // 70+ cancellation ring-fence (see CancellationRingfencePanel). Keyed off
  // the email_source the creator stamped -- never the subject text, which any
  // hand-created task could coincidentally match. `email_source` is NULL on an
  // archived row (archived_email_tasks has no such column), so an archived
  // ring-fence task renders as an ordinary task: correct, since its live
  // actions must not be offered there either.
  const isRingfenceTask = task.email_source === RINGFENCE_TASK_SOURCE;
  const canForward = !!task.assignedClaimId && !!emailContent;
  const canArchive = !task.assignedClaimId && isCompletable(task.status);
  // Explanatory mirror of the backend's blocks_unassigned_completion
  // (task_lane_service.py), which 409s a client-lane completion carrying no
  // claim link. Keyed on the `lane` prop rather than a hand-copied
  // LANE_PREDICATES['client']: UnifiedTask.type uses a different vocabulary
  // from the DB column that SQL predicate reads ('email_client' here vs
  // 'email_task' there), so re-deriving it client-side would be the brittle
  // option. The backend stays the actual guard -- rendered without the prop
  // this button just behaves as it always did, and the 409 explains itself
  // in the toast. Ring-fence tasks are exempt in both places: they bind
  // claims through claudeAnalysis.extractedData.claimIds, not
  // assignedClaimId. Note this is the exact inverse of canArchive above --
  // link it and complete it, or archive it.
  const needsClaimLinkToComplete = lane === 'client' && !task.assignedClaimId && !isRingfenceTask;
  const claudeAnalysis = content?.claudeAnalysis as Record<string, any> | null;
  // The CAF processor's structured columns are authoritative. Some older
  // rows retain a stale pre-CAF claudeAnalysis object in the content table;
  // using that object wholesale made the header show a valid classification
  // while this section showed 0% or no category/reason. Keep legacy values as
  // fallbacks for pre-CAF tasks and use its extractedData as supplementary
  // metadata only.
  const aiCategory = task.aiCategory || claudeAnalysis?.category || task.claudeAnalysis?.category || null;
  const aiConfidence = task.aiConfidence ?? claudeAnalysis?.confidence ?? task.claudeAnalysis?.confidence ?? null;
  const aiReason = task.aiReasoning
    || claudeAnalysis?.reasoning
    || claudeAnalysis?.reason
    || task.claudeAnalysis?.reasoning
    || task.claudeAnalysis?.reason
    || null;
  const extractedData = claudeAnalysis?.extractedData;
  const hasClaudeAnalysis = !!(
    aiCategory
    || aiConfidence != null
    || aiReason
    || (extractedData && Object.keys(extractedData).length > 0)
  );
  // Whole-branch review finding 3: a 70+ pre-cancellation hold task's ONLY
  // completion door is the outcome buttons inside CancellationRingfencePanel
  // (keep / cancel+charge / cancel+waive) -- that panel's own handleComplete
  // call goes through the pane's onComplete prop, which is unaffected. The
  // generic Complete button/hotkey below hit POST /complete directly, which
  // the backend now 409s for exactly this task (is_pre_cancellation_hold_
  // task) -- a dead-end button/hotkey otherwise. Same holdType check as
  // CancellationRingfencePanel's own isHoldTask.
  const isPreCancellationHold = claudeAnalysis?.extractedData?.holdType === 'pre_cancellation';
  // Task 6 fix round (2026-08-25): carry the task's own received date onto
  // the claim deep link so a staff member cancelling from here lands with
  // the date already typed in -- otherwise the cancel is judged at today,
  // which is exactly the over-charge defect Task 6 exists to fix. Only
  // appended when receivedDate actually parses; an unparseable/missing date
  // leaves the link unchanged (today's behaviour).
  const claimLinkHref = task.assignedClaimId
    ? buildClaimLinkHref(task.assignedClaimId, emailContent?.receivedDate, emailContent?.from)
    : null;
  // Wrong-claim attach guard (2026-09-28): the email's own references --
  // AI-extracted first, then subject + body (content endpoint), then the
  // subject-only list from the task row in case content didn't load.
  // Deploy-order safety: a backend that predates the guard sends none of
  // these keys -- `known` is false and the picker opens exactly as it did
  // before the guard (no note, no pre-fill, no confirm).
  const { known: emailRefsKnown, refs: emailRefs } = resolveEmailRefs(
    task.aiClaimReference,
    content?.referencedClaimRefs,
    task.subjectClaimRefs,
  );
  const suggestedRef = normaliseClaimRef(suggestedClaimRef);
  const claimPrefill = pickPrefill(task.aiClaimReference, emailRefs, task.assignedClaimId ? null : suggestedRef);
  const getAssignConfirmation = emailRefsKnown
    ? (claim: Claim) =>
      buildSingleAssignConfirmation(claim.id, emailRefs, attachedRecentlyElsewhere(claim.id, task.id))
    : undefined;

  return (
    <div className="task-detail">
      <div className="task-detail__header">
        <h3 className="task-detail__subject">
          {renderSubject ? renderSubject(task.subject || null) : (task.subject || '(no subject)')}
        </h3>
        <div className="task-detail__meta">
          {task.clientName && <span>{task.clientName}</span>}
          <span>{formatDateTime(emailContent?.receivedDate || task.createdAt)}</span>
          <span className={`task-detail__status task-detail__status--${task.status}`}>{statusLabel(task.status)}</span>
        </div>
        <div className="task-detail__claim-row">
          {task.assignedClaimId ? (
            /* Demo finding: this used to link to /claim-management, which
               is the "claims without an LOC" tool, not the claims table --
               wrong destination for "open this claim". The claims drawer
               lives on ClaimsTable at "/" (see App.tsx), which already
               supports opening a claim on mount via navigation state
               (CreateClaim.tsx uses the same `{ openClaimId }` convention
               after creating a claim). task.assignedClaimId is the claim
               document's own id (suffix like ".002" included for
               multi-agreement claims), which is exactly what getClaim()
               expects -- no stripping needed.

               Ticket #403: this used to be a router-state `<Link to="/"
               state={{ openClaimId }}>`, which navigated in the SAME tab --
               a staff member working a task lost their place (the lane/
               task list they were on) every time they opened a linked
               claim. It's now a real anchor to `/?openClaimId=<id>` with
               target="_blank" -- a genuine new-tab open, so click, Cmd/
               Ctrl-click and middle-click all behave exactly the way a
               normal link does (the browser handles the modifier, nothing
               to special-case here). ClaimsTable's mount effect (below)
               now reads `openClaimId` from either the query string or nav
               state, so the deep link works standalone in the new tab
               with no state to carry over. */
            <a
              className="task-detail__claim-chip"
              href={claimLinkHref || `/?openClaimId=${encodeURIComponent(task.assignedClaimId)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Claim {task.assignedClaimId} &#8599;
            </a>
          ) : (
            <>
              <span className="task-detail__no-claim-flag">No claim linked</span>
              {suggestedRef && <span className="task-detail__suggest">Would attach to {suggestedRef}</span>}
            </>
          )}
          {aiCategory && (
            <span className="task-detail__ai-badge">
              {aiCategory}
              {aiConfidence != null ? ` (${Math.round(aiConfidence * 100)}%)` : ''}
            </span>
          )}
          {task.assignedToTeamMember && (
            <span className="lane-assignee-pill">{task.assignedToTeamMember}</span>
          )}
          {task.workflow?.admission_kind === 'example' && <span className="lane-ai-status-badge">
            {replacementRunLabel(task.workflow)} · {task.workflow.name || 'Workflow'} version {task.workflow.version}
          </span>}
        </div>
      </div>

      {/* Actions moved here (directly under the header block) so they're
          immediately accessible without scrolling past the email body --
          previously this row sat at the very bottom of the pane, after the
          full email content section, which on a long email meant scrolling
          the whole body just to Complete/Archive/Assign. `.task-detail__body`
          below still has `flex: 1 1 auto` and is still the pane's only
          scrolling region -- moving this row earlier in DOM order doesn't
          change the flex layout, it just makes the row (and everything
          above it: header/meta/claim-row) part of the fixed, always-visible
          top section instead of a pinned-bottom row. */}
      <div className="task-detail__actions">
        {task.assignedClaimId ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)}>
              Reassign claim
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUnassignClaim}
              disabled={unassigningClaim}
            >
              {unassigningClaim ? 'Removing…' : 'Remove from claim'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)}>
            Assign claim
          </Button>
        )}
        {canForward && (
          <Button variant="secondary" size="sm" onClick={() => setForwardOpen(true)}>
            Forward to client
          </Button>
        )}
        {isCompletable(task.status) && !isPreCancellationHold && (
          // Disabled rather than hidden: a missing button reads as "this
          // isn't a thing you can do", where a disabled one with a reason
          // reads as "do this first". The tooltip is the whole explanation.
          <Button
            variant="primary"
            size="sm"
            onClick={handleComplete}
            disabled={completing || needsClaimLinkToComplete}
            title={needsClaimLinkToComplete
              ? "Assign this email to a claim first — completing it unlinked would leave no record on the claim's history"
              : "Press 'c' to complete"}
          >
            Complete
          </Button>
        )}
        {canArchive && (
          <Button variant="ghost" size="sm" onClick={() => setArchiveOpen(true)}>
            Archive
          </Button>
        )}
        {/* Item 2 (lane-feedback batch 28/07, Ryan decision): no longer
            admin-only -- POST /ui/team-tasks/bulk-assign (which this button
            drives) moved from require_role(['admin']) to require_auth, so
            any authenticated staff member can assign/reassign a task to
            any roster member, not just admins. Was `isAdmin &&`. */}
        <Button variant="secondary" size="sm" onClick={() => setAssignMemberOpen(true)}>
          {task.assignedToTeamMember ? 'Reassign staff' : 'Assign to staff'}
        </Button>
        {actionsExtra?.(task)}
      </div>

      {/* Demo finding: "for completed emails we're missing a lot of the
          details of who completed it and when it was completed." Shown
          whenever completedAt is present -- not gated on task.status, since
          the completed-tab status set also includes 'approved'/'rejected'
          (task_lane_service.STATUS_TAB_MAP), and an archived-but-previously-
          completed row should still show this alongside the archive info
          below. */}
      {task.completedAt && (
        <div className="task-detail__completed-info">
          <strong>Completed</strong>
          {task.completedBy && <> by <ActorLabel raw={task.completedBy} /></>}
          {' · '}{formatDateTime(task.completedAt)}
        </div>
      )}

      {task.archivedAt && (
        <div className="task-detail__archive-info">
          <strong>Archived</strong> {formatDateTime(task.archivedAt)}
          {task.archivedBy && <> by <ActorLabel raw={task.archivedBy} /></>}
          {task.archiveReason && <p className="task-detail__archive-reason">{task.archiveReason}</p>}
        </div>
      )}

      {isRingfenceTask && (
        <CancellationRingfencePanel
          key={task.id}
          task={task}
          content={content}
          showToast={showToast}
          onChanged={onChanged}
          onComplete={completeTaskNow}
          canAct={isCompletable(task.status) && !task.archivedAt}
        />
      )}

      {/* A ring-fence task is `type: 'manual'` but has no `description` column
          (create_email_task never writes one), so this generic block would
          render an empty "Task details" box under the panel above -- the panel
          already carries the narrative body. */}
      {task.type === 'manual' && !isRingfenceTask && (
        <div className="task-detail__section">
          <h4>Task details</h4>
          {task.description && <p className="task-detail__description">{task.description}</p>}
          {task.dueDate && <div className="task-detail__row"><strong>Due:</strong> {task.dueDate}</div>}
          {task.priority && <div className="task-detail__row"><strong>Priority:</strong> {task.priority}</div>}
          {!content && loading && <div className="task-detail__hint">Loading task details…</div>}
        </div>
      )}

      {isEmailType && (
        <div className="task-detail__section">
          <h4>Email content</h4>
          {!emailContent && loading && <div className="task-detail__hint">Loading email content…</div>}
          {!emailContent && !loading && <div className="task-detail__hint">No email content available.</div>}
          {emailContent && (
            <>
              {emailContent.isEncrypted && (
                <div className="task-detail__encrypted-warning">
                  <span>
                    🔒 <strong>Encrypted email</strong> — this message is protected by Microsoft
                    Purview encryption and cannot be read here. Open it in Outlook to view the content.
                  </span>
                  {emailContent.webLink && (
                    <a
                      href={emailContent.webLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="task-detail__outlook-link"
                    >
                      🔒 Open in Outlook
                    </a>
                  )}
                </div>
              )}
              <div className="task-detail__row"><strong>From:</strong> {emailContent.from}</div>
              <div className="task-detail__row"><strong>To:</strong> {emailContent.to}</div>
              {/* Freshdesk #656: the email renders in a sandboxed frame so a
                  lender's stylesheet (Ford's dark-mode rules) can't recolour
                  the pane or the rest of PCP. See EmailBodyFrame. */}
              <div className="task-detail__body task-detail__body--frame">
                <EmailBodyFrame
                  className="task-detail__body-frame"
                  html={resolveCidImages(emailContent.body || '<p>No content</p>', emailContent.attachments)}
                />
              </div>
              {emailContent.attachments && emailContent.attachments.length > 0 && (
                <div className="task-detail__attachments">
                  <strong>Attachments ({emailContent.attachments.length}):</strong>
                  {emailContent.attachments.map((att: any, i: number) => (
                    <div key={i} className="task-detail__attachment">
                      <span>📎 {att.name || att.filename || `Attachment ${i + 1}`}</span>
                      {att.size && <span className="task-detail__attachment-size">({formatFileSize(att.size)})</span>}
                      {att.secureUrl && (
                        <a href={att.secureUrl} download={att.name || `attachment_${i + 1}`} target="_blank" rel="noopener noreferrer">
                          Download
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isEmailType && hasClaudeAnalysis && (
        <details className="task-detail__section task-detail__ai-section">
          <summary>AI classification</summary>
          <div className="task-detail__ai-content">
            {aiCategory && (
              <div className="task-detail__row">
                <strong>Category:</strong> {aiCategory}
              </div>
            )}
            {aiConfidence != null && (
              <div className="task-detail__row">
                <strong>Confidence:</strong> {(Number(aiConfidence) * 100).toFixed(1)}%
              </div>
            )}
            {aiReason && (
              <div className="task-detail__row"><strong>Reason:</strong> {aiReason}</div>
            )}
            {extractedData && Object.keys(extractedData).length > 0 && (
              <div className="task-detail__row">
                <strong>Extracted data:</strong>
                <pre className="task-detail__ai-json">{JSON.stringify(extractedData, null, 2)}</pre>
              </div>
            )}
          </div>
        </details>
      )}

      {footerExtra?.(task)}

      {isAdmin && task.workflow?.admission_kind === 'example' && <button type="button"
        className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => setShowWorkflowRun(true)}>
        Open workflow run
      </button>}
      {showWorkflowRun && isAdmin && task.workflow?.admission_kind === 'example' &&
        <AutomationRunDrawer runId={task.workflow.run_id} onClose={() => setShowWorkflowRun(false)} onChanged={onChanged} />}

      {assignOpen && (
        <ClaimPickerModal
          title={task.assignedClaimId ? 'Reassign to a different claim' : 'Assign to a claim'}
          hint={task.subject || undefined}
          confirmLabel={task.assignedClaimId ? 'Reassign' : 'Assign'}
          processing={assigning}
          initialTerm={claimPrefill.term}
          suggestions={claimPrefill.chips}
          note={emailRefsKnown && isEmailType && emailRefs.length === 0 ? NO_REFERENCE_NOTE : undefined}
          getConfirmation={getAssignConfirmation}
          onConfirm={handleAssignConfirm}
          onClose={() => setAssignOpen(false)}
        />
      )}

      {archiveOpen && (
        <ArchiveReasonModal
          title="Archive task"
          confirmLabel="Archive task"
          processing={archiving}
          onConfirm={handleArchiveConfirm}
          onClose={() => setArchiveOpen(false)}
        />
      )}

      {forwardOpen && (
        <ForwardComposer
          taskId={task.id}
          showToast={showToast}
          onClose={() => setForwardOpen(false)}
          onSent={() => { setForwardOpen(false); onChanged?.(); }}
        />
      )}

      {assignMemberOpen && (
        <MemberPickerModal
          title={task.assignedToTeamMember ? 'Reassign to a different staff member' : 'Assign to a staff member'}
          hint={task.subject || undefined}
          confirmLabel={task.assignedToTeamMember ? 'Reassign' : 'Assign'}
          currentAssignee={task.assignedToTeamMember}
          lane={lane}
          processing={assigningMember}
          onConfirm={handleAssignMemberConfirm}
          onClose={() => setAssignMemberOpen(false)}
        />
      )}
    </div>
  );
};

export default TaskDetailPane;
